import { useEffect, useSyncExternalStore } from 'react'

/**
 * Keep `--keyboard` equal to how much of the bottom of the screen the
 * on-screen keyboard covers.
 *
 * On iOS the keyboard does not resize the layout viewport. It shrinks the
 * visual one and may pan it, so a `position: fixed; bottom: 0` sheet stays
 * glued to the bottom of a screen the keyboard is now sitting on. The visual
 * viewport is the only thing that reports the difference: whatever of the
 * layout viewport lies below the visual one's bottom edge is keyboard.
 *
 * Android Chrome gets `interactive-widget=resizes-content` in the viewport
 * meta instead, which resizes the layout; there this settles on 0.
 */
export function useKeyboardInset(): void {
  useEffect(() => {
    const viewport = window.visualViewport
    if (!viewport) return
    const root = document.documentElement
    let frame = 0

    const apply = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        const covered = window.innerHeight - (viewport.height + viewport.offsetTop)
        // Sub-pixel noise and the iOS toolbar collapsing are not a keyboard.
        const inset = covered > 60 ? Math.round(covered) : 0
        root.style.setProperty('--keyboard', `${inset}px`)
        root.toggleAttribute('data-keyboard', inset > 0)
      })
    }

    apply()
    viewport.addEventListener('resize', apply)
    viewport.addEventListener('scroll', apply)
    return () => {
      cancelAnimationFrame(frame)
      viewport.removeEventListener('resize', apply)
      viewport.removeEventListener('scroll', apply)
      root.style.removeProperty('--keyboard')
      root.removeAttribute('data-keyboard')
    }
  }, [])
}

/**
 * Hold the screen awake while `active`.
 *
 * A wake lock is dropped by the browser whenever the page is hidden (a call
 * coming in, the app switcher), so it is asked for again each time the page
 * comes back. Where the API is missing, filming still works; the phone just
 * dims on its own schedule.
 */
export function useWakeLock(active: boolean): void {
  useEffect(() => {
    if (!active || !('wakeLock' in navigator)) return
    let lock: WakeLockSentinel | null = null
    let cancelled = false

    const acquire = async () => {
      if (document.visibilityState !== 'visible' || (lock && !lock.released)) return
      try {
        const next = await navigator.wakeLock.request('screen')
        if (cancelled) void next.release()
        else lock = next
      } catch {
        // Refused (low battery, no user gesture yet). Nothing to fix from here.
      }
    }

    void acquire()
    document.addEventListener('visibilitychange', acquire)
    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', acquire)
      void lock?.release()
    }
  }, [active])
}

const WIDE = '(min-width: 900px)'

function subscribeWide(onChange: () => void): () => void {
  const query = window.matchMedia(WIDE)
  query.addEventListener('change', onChange)
  return () => query.removeEventListener('change', onChange)
}

/**
 * Whether the screen is a laptop's: from 900px the bottom bar is a rail, the
 * pipeline is a canvas, and an idea opens in a panel beside it rather than
 * as a page of its own.
 */
export function useWide(): boolean {
  return useSyncExternalStore(
    subscribeWide,
    () => window.matchMedia(WIDE).matches,
    () => false,
  )
}
